import * as React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';

import { Button, Container, Icon, Section, Title, Loader, OrderBox } from '../../modules/modules';
import { Query, compose, graphql, MutationFunc } from 'react-apollo';
import gql from 'graphql-tag';
import * as queryString from 'query-string';
import ErrorPage from '../ErrorPage';
import CountryService from '../../services/CountryService';
import OrderPayButton from '../../components/OrderPayButton';
import { CurrencyFormatter as Currency } from '../../utils/utils';
import VatService from '../../services/VatService';
import { getOrderVat } from '../../helpers/orders/getOrderVat';
import { CreatePaymentMutation } from '../../components/Query/Payment/CreatePayment';
import { SetPaymentStatusMutation } from '../../components/Query/Payment/SetPaymentStatus';
import { getPaymentFailed } from '../../helpers/payment/getPaymentFailed';
import { getPaymentSuccessful } from '../../helpers/payment/getPaymentSuccessful';
import { getPaymentPending } from '../../helpers/payment/getPaymentPending';

import GoogleTagManagerService from '../../services/GoogleTagManagerService';

const usePageLanguage: boolean = true;

interface IOrderFinishedProps extends RouteComponentProps<{ 
  language: string; 
  orderNumber: string | undefined; 
  orderId: string | undefined;
  nextData: string | undefined;
}> {
  createPayment: MutationFunc;
  setPaymentStatus: MutationFunc;
}

const GetOrderPaymentState = gql`
  query orderPaymentState($orderNumber: String!, $payId: String, $payload: String) {
    orderPaymentState(orderNumber: $orderNumber, payId: $payId, payload: $payload) {
      order {
        id
        orderNumber
        email
        language
        total
        paymentTotal
        deliveryTotal
        deliveryInsuranceTotal
        grandTotal
        currency {
          id
          value
        }
        country {
          id
          code
        }
        orderedItems {
          id
          title
          name
          price
          quantity
          definition
          sku
          ean
          VATrate
          image {
            id
            file
          }
          variant {
            id
            ean
            product {
              id
              categories {
                id
                name
              }
              manufacturer {
                id
                name
              }
            }
          }
          GTMItemData
        }
        paymentMethod {
          id
          title
          name
          gateway
        }
        deliveryMethod {
          id
          title
          name
        }
      }
      payment {
        id
        payId
        paid
        amount
        status
        description
      }
    }
  }
`;

interface IOrderFinishedState {
  displayDetail: boolean;
  executedPaymentProcess: boolean;
}

class OrderFinished extends React.Component<IOrderFinishedProps, IOrderFinishedState> {

  constructor(props: IOrderFinishedProps) {
    super(props);

    this.state = {
      displayDetail: false,
      executedPaymentProcess: false,
    };
  } 

  public render() {
    const { countryCode } = CountryService.GetInstance();

    const params = queryString.parse(this.props.location.search);

    if (this.props.match.params.orderNumber) {
      // id objednávky z url
      const paramOrderId = this.props.match.params && this.props.match.params.orderId ? this.props.match.params.orderId : null;

      return (
        <main>
          <Section type="orderFinished">
            <Query
              query={GetOrderPaymentState}
              variables={{
                orderNumber: this.props.match.params.orderNumber,
                payId: (params.payId ? params.payId : null),
                // todo: sjednotit, 
                // todo: params.payload používá tuším ČSOB
                // todo: do GP webpay posílám všechno z URL adresy !!! toto je lepší řešení, každá platební brána si z vezme přesně co potřebuje ...
                payload: (params.payload ? params.payload : this.props.location.search),
              }}
              fetchPolicy="network-only"
            >
              {({ loading, error, data, refetch }) => {
                if (loading) {
                  return <Loader />;
                }

                // neznámá objednávka
                if (!data.orderPaymentState) {
                  return (<Redirect to={`/${countryCode}`} />);
                }

                // objekt objednávky
                const order = data.orderPaymentState.order;
                const payment = data.orderPaymentState.payment;

                // parametr orderId neexistuje, ale parametr orderId je korektní payId platby objednávky
                if (order && order.id && (order.id !== paramOrderId) && order.country && ('string' === typeof order.country.code) && payment && (payment.payId === paramOrderId)) {
                  return (<Redirect to={`/${order.country.code}/order-finished/${order.orderNumber}/${order.id}`} />);
                }

                if (order && order.id && (order.id !== paramOrderId) && payment && (payment.payId === paramOrderId)) {
                  return (<Redirect to={`/${countryCode}/order-finished/${order.orderNumber}/${order.id}`} />);
                }

                // pokus o hack náhledu na jinou objednávku
                if (order && order.id && (order.id !== paramOrderId)) {
                  return (<ErrorPage title={`You do not have permission to view this order`} />);
                }

                // do GTM odešle objednávku
                GoogleTagManagerService.order(order);

                if (order) {
                  // todo: ošetřit neexistující objednávku
                  const orderEmail = order.email;
                  const orderOrderNumber = order.orderNumber;

                  // platba online
                  if (order.paymentMethod && order.paymentMethod.gateway) {

                    // platba nebyla dosud založena
                    // nebo byla zalozena, ale není zaplacena
                    // if (!payment || (payment && !payment.paid)) {
                    if (!getPaymentSuccessful(payment)) {
                      // děkovací mezistránka
                      return (
                        <React.Fragment>
                          <Container type="xs">
                            <div className="text-center">
                              {!getPaymentFailed(payment) &&
                                <>
                                  <Icon type="confirm" />
                                  <Title>
                                    Thank you for your order
                                  </Title>
                                  <p className="mt-3">We have sent a confirmation to your e-mail address at {orderEmail}.</p>
                                </>}
                              {getPaymentFailed(payment) &&
                                <>
                                  <Icon type="cancel" className={'mb-4'} />
                                  <Title>
                                    {payment.status === 'canceled' &&
                                      `Payment canceled`}
                                    {payment.status !== 'canceled' &&
                                      `Payment failed`}
                                  </Title>
                                </>}
                              <p className="text-lg text-bold mb-0 mt-3 pb-0">Order No.: <strong>{orderOrderNumber}</strong></p>

                              <div className="payBox">
                                <div className="orderBox__infoItem orderBox__infoItem--final">
                                  <div className="orderBox__price text-center-force">
                                    <span className="orderBox__label">Total price {VatService.GetInstance().displayIncludeVat(getOrderVat(order), order.country)}:</span>
                                    <span>
                                      <Currency price={order.grandTotal} currency={order.currency} />
                                    </span>
                                  </div>
                                </div>
                                
                                <OrderPayButton
                                  order={order}
                                  payment={payment}
                                  nextData={this.props.match.params.nextData} // todo: fuj !!!
                                  onClick={() => this.executePaymentProcess(order)}
                                  onRefetch={refetch}
                                  handleCreatePayment={this.props.createPayment}
                                  setPaymentStatus={this.props.setPaymentStatus}
                                />
                              </div>

                              {this.state.executedPaymentProcess &&
                                <React.Fragment>
                                  <p className="mt-3">
                                    Redirecting to payment gateway ... 
                                  </p>
                                  <Loader />
                                </React.Fragment>}
                            </div>
                          </Container>
                        </React.Fragment>
                      );
                    }

                    // objednávka (platba) zaplacena
                    if (getPaymentSuccessful(payment)) {
                      // redirect z url s parametry
                      if (params.payId) {
                        return (<Redirect to={`/${countryCode}/order-finished/${orderOrderNumber}/${paramOrderId}`} />);
                      }

                      return (
                        <React.Fragment>
                          <Container type="xs">
                            <div className="text-center">

                              <Icon type="confirm" />
                              <Title>
                                Thank you for your order.<br />
                                {getPaymentPending(payment) ? 'We are waiting for payment confirmation' : 'Your payment was successful.'}
                              </Title>
                              <p className="mt-3">We have sent a confirmation to your e-mail address at {orderEmail}.</p>
                              <p className="text-lg text-bold mb-0 mt-3 pb-0">Order No.: {orderOrderNumber}</p>

                              <div className="mt-4">
                                <Button
                                  color="trans"
                                  size="lg"
                                  onClick={() => this.props.history.push(`/${countryCode}`)}
                                >
                                  Go to homepage
                                </Button>
                              </div>
                            </div>
                          </Container>
                        </React.Fragment>
                      );
                    }
                  }

                  // objednávka dokončena
                  // platba dobírkou a jiné
                  return (
                    <React.Fragment>
                      <Section type="bordered">
                        <Container>
                          <div className="row">
                            <div className="offset-1 col-10 offset-md-3 col-md-6">
                              <div className="text-center">
                                <Icon type="confirm" className="mb-4" />
                                <Title type="thin">
                                  Thank you for your order
                                </Title>
                                <span>
                                  We have sent a confirmation to your e-mail address at {orderEmail}. <br />
                                  <br />
                                  Order No.: <strong>{orderOrderNumber}</strong>
                                </span>

                                <OrderBox
                                  className="mt-4"
                                  type="order"
                                  order={order}
                                  products={order.orderedItems}
                                  price={{
                                    Shipment: order.deliveryTotal,
                                    Payment: order.paymentTotal,
                                    Total: order.total,
                                    DeliveryInsurance: order.deliveryInsuranceTotal,
                                  }}
                                  payment={order.paymentMethod}
                                  delivery={order.deliveryMethod}
                                />

                                <div className="mt-2 mt-3-md">
                                  <Button
                                    color="trans"
                                    size="lg"
                                    onClick={() => this.props.history.push(`/${countryCode}`)}
                                  >
                                    Go to homepage
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Container>
                      </Section>
                    </React.Fragment>
                  );
                } else {

                  return null;
                }
              }}
            </Query>
          </Section>
        </main>
      );
    }

    return (<Redirect to={`/${countryCode}`} />);
  }

  /**
   * Založení platby
   * 
   * @param order 
   */
  private async executePaymentProcess(order: any) {

    // todo: pokud je povolen příjem chyby, celé se to zacyklí
    // todo: nutno přidat ošetření aby se tato fce pouštěla jen 1x !!!!
    if (this.state.executedPaymentProcess === false) {
      this.setState({
        executedPaymentProcess: true,
      }, async () => {

        // vytvoření platby
        const createPaymentResult = await this.props.createPayment({
          variables: {
            orderNumber: order.orderNumber,
            language: usePageLanguage ? CountryService.GetInstance().language : order.language,
            paymentMethod: order.paymentMethod.id,
          }
        });
    
        // chyba platby
        if (createPaymentResult.data
          && createPaymentResult.data.createPayment
          && createPaymentResult.data.createPayment.error
        ) {
          // alert(createPaymentResult.data.createPayment.error);
          // todo: report to FoxMedia
        }
    
        // todo: processing dle platební brány
        if (createPaymentResult.data
          && createPaymentResult.data.createPayment
          && createPaymentResult.data.createPayment.gatewayUrl
        ) {
          window.location.href = createPaymentResult.data.createPayment.gatewayUrl;
        }

      });
    }
  }
}

export default compose(
  graphql<any, any>(CreatePaymentMutation, {
    name: 'createPayment'
  }),
  graphql<any, any>(SetPaymentStatusMutation, {
    name: 'setPaymentStatus',
  }),
)(OrderFinished);



// WEBPACK FOOTER //
// ./src/pages/OrderFinished/OrderFinished.tsx